/**
 * Simulation datasets: build one from ticked scenarios, add more of them later, read it with its
 * cases, edit or delete it and its cases, run it and evaluate the run. The backend captures each
 * case, and runs the dataset, in the background and reports over the socket.
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type {
	CreateSimulationDatasetInput,
	DatasetCaseCapture,
	DatasetCaseEvaluation,
	DatasetExpectationsPatch,
	DatasetExpectedSources,
	DatasetRun,
	DatasetRunDetail,
	DatasetRunPage,
	DatasetSourceName,
	SimulationDataset,
	SimulationDatasetCase,
	SimulationDatasetDetail,
	SimulationDatasetListItem,
	SimulationDatasetPatch
} from '@/types/simulation-dataset.types';

const EVALUATE_TIMEOUT_MS = 90_000;

export const simulationDatasetService = {
	/** `buId` is the dashboard's brand; the dataset takes the SOP's own, and the backend refuses one that contradicts it. */
	async create(input: CreateSimulationDatasetInput, buId?: string | null): Promise<SimulationDatasetDetail> {
		const response = await api.post<ApiResponse<SimulationDatasetDetail>>(API_ENDPOINTS.SIMULATION_DATASETS.BASE, input, {
			params: buId ? { bu_id: buId } : undefined
		});
		return response.data.data;
	},

	/** Adds 1 to 20 of the type's scenarios, none already a case; answers the new cases only, queued after the last one. */
	async addCases(datasetId: string, scenarioIds: string[]): Promise<SimulationDatasetDetail> {
		const response = await api.post<ApiResponse<SimulationDatasetDetail>>(API_ENDPOINTS.SIMULATION_DATASETS.CASES(datasetId), {
			scenarioIds
		});
		return response.data.data;
	},

	/** The brand's datasets and the shared ones, newest first; no brand lists the whole account's. */
	async list(buId?: string | null): Promise<SimulationDatasetListItem[]> {
		const response = await api.get<ApiResponse<{ datasets: SimulationDatasetListItem[] }>>(
			API_ENDPOINTS.SIMULATION_DATASETS.BASE,
			{ params: buId ? { bu_id: buId } : undefined }
		);
		return response.data.data.datasets;
	},

	async fetch(datasetId: string): Promise<SimulationDatasetDetail> {
		const response = await api.get<ApiResponse<SimulationDatasetDetail>>(API_ENDPOINTS.SIMULATION_DATASETS.BY_ID(datasetId));
		return response.data.data;
	},

	/** Renames it or changes its description; only the fields given change. */
	async update(datasetId: string, patch: SimulationDatasetPatch): Promise<SimulationDataset> {
		const response = await api.patch<ApiResponse<{ dataset: SimulationDataset }>>(
			API_ENDPOINTS.SIMULATION_DATASETS.BY_ID(datasetId),
			patch
		);
		return response.data.data.dataset;
	},

	/** The dataset and every case in it. */
	async remove(datasetId: string): Promise<void> {
		await api.delete(API_ENDPOINTS.SIMULATION_DATASETS.BY_ID(datasetId));
	},

	/** What the run that captured each case drew on, for the whole dataset at once. */
	async fetchCaptures(datasetId: string): Promise<DatasetCaseCapture[]> {
		const response = await api.get<ApiResponse<{ captures: DatasetCaseCapture[] }>>(
			API_ENDPOINTS.SIMULATION_DATASETS.CAPTURES(datasetId)
		);
		return response.data.data.captures;
	},

	/** Names for the sources the dataset shows, including ones switched off or deleted since. */
	async fetchSourceNames(datasetId: string, ids: DatasetExpectedSources): Promise<DatasetSourceName[]> {
		const params = Object.fromEntries(
			Object.entries(ids)
				.filter(([, list]) => list.length > 0)
				.map(([kind, list]) => [kind, list.join(',')])
		);
		const response = await api.get<ApiResponse<{ names: DatasetSourceName[] }>>(
			API_ENDPOINTS.SIMULATION_DATASETS.SOURCE_NAMES(datasetId),
			{ params }
		);
		return response.data.data.names;
	},

	/** Refused with a 409 while the case is still being captured. */
	async updateCase(datasetId: string, caseId: string, expected: DatasetExpectationsPatch): Promise<SimulationDatasetCase> {
		const response = await api.patch<ApiResponse<{ case: SimulationDatasetCase }>>(
			API_ENDPOINTS.SIMULATION_DATASETS.CASE(datasetId, caseId),
			expected
		);
		return response.data.data.case;
	},

	async removeCase(datasetId: string, caseId: string): Promise<void> {
		await api.delete(API_ENDPOINTS.SIMULATION_DATASETS.CASE(datasetId, caseId));
	},

	/**
	 * Runs every case once; refused with a 409 while a run is going or a case is still being prepared.
	 * `buId` is the dashboard's brand, as for creating one.
	 */
	async startRun(datasetId: string, buId?: string | null): Promise<DatasetRun> {
		const response = await api.post<ApiResponse<{ run: DatasetRun }>>(
			API_ENDPOINTS.SIMULATION_DATASETS.RUNS(datasetId),
			undefined,
			{ params: buId ? { bu_id: buId } : undefined }
		);
		return response.data.data.run;
	},

	/**
	 * The run named by `runId`, or with none the latest, and what each case produced in it; no run before
	 * the dataset first ran.
	 */
	async fetchRun(datasetId: string, runId: string | null): Promise<DatasetRunDetail> {
		const response = await api.get<ApiResponse<DatasetRunDetail>>(
			runId
				? API_ENDPOINTS.SIMULATION_DATASETS.RUN_BY_ID(datasetId, runId)
				: API_ENDPOINTS.SIMULATION_DATASETS.RUN(datasetId)
		);
		return response.data.data;
	},

	/** The dataset's runs, newest first. */
	async fetchRuns(datasetId: string, offset: number, limit: number): Promise<DatasetRunPage> {
		const response = await api.get<ApiResponse<DatasetRunPage>>(API_ENDPOINTS.SIMULATION_DATASETS.RUNS(datasetId), {
			params: { offset, limit }
		});
		return response.data.data;
	},

	/**
	 * Checks what the run produced for the case against its answer key. Refused with a 409 before the case
	 * has run and while it runs. Judging the reply can outlast the default timeout.
	 */
	async evaluateCase(datasetId: string, caseId: string, runId: string): Promise<DatasetCaseEvaluation> {
		const response = await api.post<ApiResponse<{ evaluation: DatasetCaseEvaluation }>>(
			API_ENDPOINTS.SIMULATION_DATASETS.CASE_EVALUATION(datasetId, caseId),
			{ runId },
			{ timeout: EVALUATE_TIMEOUT_MS }
		);
		return response.data.data.evaluation;
	},

	/** One run's evaluations. */
	async fetchEvaluations(datasetId: string, runId: string): Promise<DatasetCaseEvaluation[]> {
		const response = await api.get<ApiResponse<{ evaluations: DatasetCaseEvaluation[] }>>(
			API_ENDPOINTS.SIMULATION_DATASETS.EVALUATIONS(datasetId),
			{ params: { runId } }
		);
		return response.data.data.evaluations;
	}
};
